import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect, batch } from 'react-redux';
import EventEmitter from 'wolfy87-eventemitter';

import { makeGetMasterToken, makeGetTeamToken, makeGetTeamUrl } from '../selectors/auth';
import { makeGetCurrentUserId } from '../selectors/users';
import { makeGetCurrentTeam } from '../selectors/teams';

import { setSocketErrorState } from '../actions/app-props';

import { getDeviceId } from 'shared-services/device-service';
import SocketService from 'shared-services/socket-service';
import { debugLog as log } from 'shared-services/logger-service';

import SocketEventsRestorer from './SocketEventsRestorer';
import SocketListeners from './SocketListeners';

import { SocketProvider } from '../context/SocketContext';

const IGNORED_EVENTS = ['message_typing_start', 'message_typing_end'];
const ACTIONS_APPLY_DELAY = 200;

class MainSocket extends PureComponent {
    static propTypes = {
        host: PropTypes.string,
        masterToken: PropTypes.string,
        teamToken: PropTypes.string,
        currentUserId: PropTypes.string,
        currentTeamId: PropTypes.string
    };

    eventEmitter = new EventEmitter();
    pendingActions = [];

    state = {
        socket: null
    }

    async connect() {
        log('[SOCKET]', 'Connect');
        if (this.state.socket) {
            log('[SOCKET]', 'Disconnect from existing connection');
            this.disconnect();
        }

        const socket = await SocketService({
            host: this.props.host,
            token: this.props.masterToken,
            deviceId: getDeviceId(),
            onError: () => {
                setSocketErrorState(true);
            },
            onConnect: lastSocketEventTimestamp => {
                if (lastSocketEventTimestamp && this.eventsRestoreCtrl) {
                    this.eventsRestoreCtrl.restore(lastSocketEventTimestamp);
                }

                setSocketErrorState(false);
            }
        });

        log('[SOCKET]', 'Join team');

        this.emitEvent('join_team', {
            token: this.props.teamToken
        });

        this.bind(socket);
        this.setState({ socket });
    }

    bind(socket) {
        socket.on('event', event => {
            this.broadcastEvents([event]);
        });
    }

    broadcastEvents(events) {
        events.forEach(event => {
            if (!event.team_id || event.team_id === this.props.currentTeamId) {
                if  (!this.suspended()) {
                    log('[SOCKET]', 'event', event);
                    this.broadcastEvent(event);
                } else {
                    log('[SOCKET]', 'Socket suspended!', event);
                }
            } else {
                log('[SOCKET]', 'event from other team', event);
                log('[SOCKET]', 'current team', this.props.currentTeamId);
            }
        }); 
    }

    broadcastEvent(event) {
        this.eventEmitter.emit(event.type, event.payload || event);
    }

    emitEvent = (event, payload) => {
        if (!this.state.socket) {
            return;
        }

        this.state.socket.emit(event, payload);
    }

    applyStoreAction = storeAction => {
        this.pendingActions.push(storeAction);

        if (this.actionsFireTimer) {
            return;
        }

        this.actionsFireTimer = setTimeout(() => {
            this.actionsFireTimer = null;
            this.applyMultipleActions();
        }, ACTIONS_APPLY_DELAY);
    }

    immidiateApplyStoreAction = storeAction => {
        this.props.dispatch(storeAction);
    }

    applyMultipleActions() {
        batch(() => {
            this.pendingActions.forEach(action => {
                this.props.dispatch(action);
            });

            this.pendingActions = [];
        });
    }

    suspend() {
        this.setState({
            suspendedTimestamp: new Date().getTime()
        });
    }

    resume(disableImmediately) {
        if (!this.state.suspendedTimestamp) {
            return Promise.reject();
        }

        this.eventsRestoreCtrl && this.eventsRestoreCtrl.restore(this.state.suspendedTimestamp);

        if (disableImmediately) {
            this.suspend();
        } else {
            this.setState({
                suspendedTimestamp: null
            });
        }
    }

    handleRestoredEvents = events => {
        this.broadcastEvents(events);
    }

    suspended() {
        return Boolean(this.state.suspendedTimestamp);
    }

    disconnect() {
        this.pendingActions = [];

        if (this.state.socket) {
            log('[SOCKET]', 'Manual disconnect');
            this.state.socket.disconnect();
            this.state.socket = null;
        }
    }

    setEventsRestoreCtrl = ctrl => this.eventsRestoreCtrl = ctrl;

    componentDidMount() {
        if (this.props.teamToken && this.props.masterToken) {
            this.connect();
        }

        this.props.provideController && this.props.provideController({
            suspend: () => {
                this.suspend();
            },
            resume: disableImmediately => {
                this.resume(disableImmediately);
            }
        });
    }

    componentDidUpdate(prevProps) {
        if (prevProps.masterToken !== this.props.masterToken || prevProps.teamToken !== this.props.teamToken) {
            if (this.props.masterToken && this.props.teamToken) {
                log('[SOCKET]', 'Change connection');
                this.connect();
            } else {
                this.disconnect();
            }
        }
    }

    componentWillUnmount() {
        // Give time for the child components to send leave signals
        setTimeout(() => {
            this.disconnect();
        }, 200);

        this.props.provideController && this.props.provideController(null);
    }

    render() {
        return (
            <React.Fragment>
                {
                    this.props.teamToken ? (
                        <React.Fragment>
                            <SocketEventsRestorer teamToken={this.props.teamToken} provideController={this.setEventsRestoreCtrl} onEvents={this.handleRestoredEvents} />

                            <SocketListeners
                                eventEmitter={this.eventEmitter}
                                applyStoreAction={this.applyStoreAction}
                                immidiateApplyStoreAction={this.immidiateApplyStoreAction}
                                currentUserId={this.props.currentUserId}
                                currentTeamUrl={this.props.teamUrl}
                            />
                        </React.Fragment>
                    ) : null
                }

                <SocketProvider
                    emitEvent={this.emitEvent}
                    socketEmitter={this.eventEmitter}
                >
                    { this.props.children }
                </SocketProvider>
            </React.Fragment>
        );
    }
};

const makeMapStateToProps = () => {
    const getTeamUrl = makeGetTeamUrl();
    const getMasterToken = makeGetMasterToken();
    const getTeamToken = makeGetTeamToken();
    const getCurrentTeam = makeGetCurrentTeam();
    const getCurrentUserId = makeGetCurrentUserId();

    return ({ auth, teams, users }) => {
        const teamUrl = getTeamUrl(auth);
        const currentTeam = getCurrentTeam(teams);

        return {
            masterToken: getMasterToken(auth),
            teamToken: getTeamToken(auth, teamUrl),
            teamUrl,
            currentUserId: getCurrentUserId(users),
            currentTeamId: currentTeam && currentTeam.id
        }
    };
}

export default connect(makeMapStateToProps)(MainSocket);
